// Main app — orchestrates tier feeds, live stream, detail drawer, rule builder
//
// Runtime data/API config resolves in config.jsx.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { ComingSoon } from "./atoms.jsx";
import { RightRail } from "./alerts.jsx";
import { KS, normalizeItem } from "./api.jsx";
import { useBrowseQuery } from "./browseState.jsx";
import { DATA_MODE, TWEAKS } from "./config.jsx";
import { GOLD_SPOT, SEED_ITEMS, TIER, dealTierThreshold, makeItem, rand } from "./data.jsx";
import { DetailDrawer } from "./detail.jsx";
import { EmptyState, FeedFooter, FeedHeader, ItemCard, ListView, TierBar } from "./feed.jsx";
import { ErrorState, LoadingGrid } from "./feedStates.jsx";
import { FilterRail, FilterStrip } from "./filters.jsx";
import { Header, Ticker } from "./header.jsx";
import { DEFAULT_PUBLIC_PREFS, loadPublicPrefs, publicPrefsSnapshot, savePublicPrefs } from "./publicPrefs.jsx";
import { TweaksPanel } from "./tweaks.jsx";
import { SessionBoundary } from "./sessionUi.jsx";

const LIVE = DATA_MODE === "live";
const LIVE_REVALIDATE_MS = 5 * 60 * 1000;
const LIVE_ACTIVITY_REFRESH_MS = 60 * 1000;
const LAST_ANALYZED_TICK_MS = 30 * 1000;
const DEFAULT_FILTERS = { karat: [], type: [], includeReviewRequired: true };
const JUST_IN_MS = 5200;
const DEFAULT_PUBLIC_FEED = "todays_deals";
// Each private surface remains gated until its owning RAF phase replaces the
// corresponding data.jsx fixtures with its live store.
const FRONTEND_ENTRY_CACHE_REVISION = "entry-cache-repair-2026-07-03";
const PUBLIC_FEED_KEYS = new Set(["todays_deals", "deep_discount", "steal", "deal", "negotiable", "unscored_gold", "all"]);
const publicFeedKey = (feed) => PUBLIC_FEED_KEYS.has(feed) ? feed : DEFAULT_PUBLIC_FEED;
const defaultFilters = () => ({
  karat: [...DEFAULT_FILTERS.karat],
  type: [...DEFAULT_FILTERS.type],
  includeReviewRequired: DEFAULT_FILTERS.includeReviewRequired,
});
const parsedTimeMs = (value) => {
  const ms = value ? Date.parse(value) : NaN;
  return Number.isFinite(ms) ? ms : null;
};
const newestTimestamp = (current, incoming) => {
  const currentMs = parsedTimeMs(current);
  const incomingMs = parsedTimeMs(incoming);
  if (incomingMs == null) return current || null;
  if (currentMs == null || incomingMs > currentMs) return incoming;
  return current;
};
const lastAnalyzedAge = (timestamp, nowMs = Date.now()) => {
  const thenMs = parsedTimeMs(timestamp);
  if (thenMs == null) return "none";
  const deltaMs = Math.max(0, nowMs - thenMs);
  if (deltaMs < 60 * 1000) return "<1m";
  if (deltaMs < 60 * 60 * 1000) return `${Math.floor(deltaMs / (60 * 1000))}m`;
  if (deltaMs < 24 * 60 * 60 * 1000) return `${Math.floor(deltaMs / (60 * 60 * 1000))}h`;
  return `${Math.floor(deltaMs / (24 * 60 * 60 * 1000))}d`;
};

function ProductApp({ session, onSignIn, onSignOut, onUpdateProfile, onCloseAccount }) {
  const [initialPublicPrefs] = useState(() => loadPublicPrefs());
  const hydratedPublicPrefs = initialPublicPrefs || DEFAULT_PUBLIC_PREFS;
  const [tweaks, setTweaks] = useState(() => initialPublicPrefs
    ? {
        ...TWEAKS,
        theme: hydratedPublicPrefs.theme,
        density: hydratedPublicPrefs.density,
        feedLayout: hydratedPublicPrefs.feedLayout,
      }
    : TWEAKS);
  const signedIn = session.status === "signed_in";

  const [liveOn, setLiveOn] = useState(LIVE);
  const [active, setActive] = useState(hydratedPublicPrefs.feed === "all" ? "browse" : "deals");
  const [feed, setFeed] = useState(hydratedPublicPrefs.feed);
  const [view, setView] = useState(tweaks.feedLayout || "grid");
  const [sort, setSort] = useState(hydratedPublicPrefs.sort);
  const [tweaksOpen, setTweaksOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [items, setItems] = useState(LIVE ? [] : SEED_ITEMS);
  const [searchTerm, setSearchTerm] = useState("");
  const [comingSoon, setComingSoon] = useState(null);
  const [justInIds, setJustInIds] = useState(new Set());
  const [justInDelays, setJustInDelays] = useState(new Map());
  const [filters, setFilters] = useState(() => ({
    ...defaultFilters(),
    ...hydratedPublicPrefs.filters,
    karat: [...(hydratedPublicPrefs.filters?.karat || [])],
    type: [...(hydratedPublicPrefs.filters?.type || [])],
  }));
  const [liveStatus, setLiveStatus] = useState(LIVE ? "connecting" : "paused");
  const [lastAnalyzedAt, setLastAnalyzedAt] = useState(null);
  const [lastAnalyzedNowMs, setLastAnalyzedNowMs] = useState(() => Date.now());
  const [spot, setSpot] = useState(GOLD_SPOT);
  const [tierMetaRevision, setTierMetaRevision] = useState(0);
  const justInTimersRef = useRef([]);
  const detailFocusReturnRef = useRef(null);
  const feedBodyRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const includeReview = filters.includeReviewRequired !== false;

  const browse = useBrowseQuery({
    enabled: LIVE,
    feed: publicFeedKey(feed),
    filters,
    sort,
    searchTerm,
    includeReview,
  });

  useEffect(() => () => {
    justInTimersRef.current.forEach(clearTimeout);
    justInTimersRef.current = [];
  }, []);

  const requestRefresh = useCallback((background = false) => {
    if (LIVE) void browse.refresh({ background });
  }, [browse.refresh]);
  const markJustIn = (ids) => {
    const uniqueIds = Array.from(new Set((ids || []).filter(Boolean)));
    if (!uniqueIds.length) return;
    setJustInIds(prev => new Set([...prev, ...uniqueIds]));
    setJustInDelays(prev => {
      const next = new Map(prev);
      uniqueIds.forEach((id, index) => {
        if (!next.has(id)) next.set(id, Math.min(index, 8) * 35);
      });
      return next;
    });
    const timer = setTimeout(() => {
      setJustInIds(prev => {
        const next = new Set(prev);
        uniqueIds.forEach(id => next.delete(id));
        return next;
      });
      setJustInDelays(prev => {
        const next = new Map(prev);
        uniqueIds.forEach(id => next.delete(id));
        return next;
      });
    }, JUST_IN_MS);
    justInTimersRef.current.push(timer);
  };

  const showComingSoon = (feature, detail) => setComingSoon({
    feature,
    detail: detail || "Accounts, alerts, and saved items are staged behind this public MVP while anonymous deal browsing stays live.",
  });
  const closeComingSoon = () => setComingSoon(null);
  const updateLastAnalyzedAt = useCallback((timestamp) => {
    setLastAnalyzedAt((current) => newestTimestamp(current, timestamp));
  }, []);

  const changeFeed = (next) => {
    setSearchTerm("");
    setFeed(publicFeedKey(next));
  };

  const runSearch = (query) => {
    const q = (query || "").trim();
    if (!q) {
      setSearchTerm("");
      return;
    }
    setActive("browse");
    setFeed("all");
    setSearchTerm(q);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };
  const applyFilters = () => requestRefresh(false);
  const resetFilters = () => {
    setFilters(defaultFilters());
    setSearchTerm("");
  };
  const clearFilters = () => {
    setFilters(defaultFilters());
    setSearchTerm("");
  };
  const setTheme = useCallback((theme) => {
    if (theme !== "light" && theme !== "dim") return;
    setTweaks(prev => ({ ...prev, theme }));
    window.parent.postMessage({ type: "__edit_mode_set_keys", edits: { theme } }, "*");
  }, []);
  const setFeedLayout = useCallback((feedLayout) => {
    if (feedLayout !== "grid" && feedLayout !== "list") return;
    setTweaks(prev => ({ ...prev, feedLayout }));
  }, []);

  // theme / accent / density on root
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", tweaks.theme);
    document.documentElement.setAttribute("data-accent", tweaks.accent);
    document.documentElement.setAttribute("data-density", tweaks.density);
  }, [tweaks]);
  useEffect(() => { setView(tweaks.feedLayout); }, [tweaks.feedLayout]);
  useEffect(() => {
    savePublicPrefs(publicPrefsSnapshot({ tweaks, feed: publicFeedKey(feed), sort, filters }));
  }, [tweaks.theme, tweaks.density, tweaks.feedLayout, feed, sort, filters]);

  // edit mode bridge
  useEffect(() => {
    const onMsg = (e) => {
      if (!e.data) return;
      if (e.data.type === "__activate_edit_mode") { setTweaksOpen(true); }
      if (e.data.type === "__deactivate_edit_mode") { setTweaksOpen(false); }
    };
    window.addEventListener("message", onMsg);
    window.parent.postMessage({type:"__edit_mode_available"}, "*");
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // ====================== LIVE DATA ======================
  // Deal-tier labels and thresholds are backend-owned reference data. Fallbacks
  // from data.jsx keep the UI usable if this optional metadata request fails.
  useEffect(() => {
    if (!LIVE || !KS.getDealTiers) return;
    let alive = true;
    KS.getDealTiers()
      .then(() => { if (alive) setTierMetaRevision(n => n + 1); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!LIVE) return;
    const canonical = publicFeedKey(feed);
    if (canonical !== feed) setFeed(canonical);
  }, [feed]);

  useEffect(() => {
    if (!LIVE) return;
    setJustInIds(new Set());
    setJustInDelays(new Map());
  }, [browse.key]);

  useEffect(() => {
    if (!LIVE || browse.newItemsSeq === 0) return;
    markJustIn(browse.lastNewIds);
  }, [browse.newItemsSeq]);

  useLayoutEffect(() => {
    if (!LIVE || !feedBodyRef.current) return;
    const node = feedBodyRef.current;
    const restore = () => { node.scrollTop = browse.scrollTop || 0; };
    if (typeof requestAnimationFrame === "function") {
      const id = requestAnimationFrame(restore);
      return () => cancelAnimationFrame(id);
    }
    restore();
    return undefined;
  }, [browse.key]);

  // Safety revalidation: the public SSE stream is primary; this catches missed events.
  useEffect(() => {
    if (!LIVE) return;
    let id = null;
    const stop = () => { if (id) { clearInterval(id); id = null; } };
    const refresh = () => {
      if (document.hidden) return;
      requestRefresh(true);
    };
    const start = () => {
      stop();
      if (!document.hidden) id = setInterval(refresh, LIVE_REVALIDATE_MS);
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else { refresh(); start(); }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { stop(); document.removeEventListener("visibilitychange", onVisibility); };
  }, [requestRefresh]);

  useEffect(() => {
    if (!LIVE || !KS.getBrowseActivity) return undefined;
    let alive = true;
    const load = () => {
      if (document.hidden) return;
      KS.getBrowseActivity()
        .then((activity) => {
          if (alive) updateLastAnalyzedAt(activity && activity.lastAnalyzedAt);
        })
        .catch(() => {});
    };
    const onVisibility = () => {
      if (!document.hidden) load();
    };
    load();
    const id = setInterval(load, LIVE_ACTIVITY_REFRESH_MS);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      alive = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [updateLastAnalyzedAt]);

  useEffect(() => {
    if (!LIVE) return undefined;
    const tick = () => setLastAnalyzedNowMs(Date.now());
    tick();
    const id = setInterval(tick, LAST_ANALYZED_TICK_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!LIVE || !browse.hasMore || browse.loading || browse.revalidating || browse.loadingMore || browse.error || browse.nextPageError) return;
    const root = feedBodyRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void browse.loadMore();
    }, { root, rootMargin: "220px 0px 220px 0px" });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    browse.key,
    browse.hasMore,
    browse.loading,
    browse.revalidating,
    browse.loadingMore,
    browse.error,
    browse.nextPageError,
    browse.loadMore,
  ]);

  // Live gold spot (refreshes every 5 min, matching the API's cache window).
  useEffect(() => {
    if (!LIVE) return;
    let alive = true;
    const load = () => KS.getSpot().then(s => { if (alive) setSpot(s); }).catch(() => {});
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Live alert rules (signed-in only).
  // Account-only alert rules are Coming soon for the public MVP.

  useEffect(() => {
    if (!LIVE) return undefined;
    if (!liveOn) {
      setLiveStatus("paused");
      return undefined;
    }
    setLiveStatus("connecting");
    const unsubscribe = KS.streamPublicItems({
      onOpen: () => setLiveStatus("connected"),
      onItem: (raw) => {
        const item = normalizeItem(raw);
        if (!item) return;
        browse.mergeLiveItem(item);
        updateLastAnalyzedAt(item.processedAt);
      },
      onError: () => {
        setLiveStatus("reconnecting");
      },
    });
    return () => unsubscribe();
  }, [liveOn, browse.mergeLiveItem, updateLastAnalyzedAt]);

  // ====================== MOCK DATA (original behavior) ======================
  // Simulated stream — only when signed in + on.
  useEffect(() => {
    if (LIVE || !liveOn || !signedIn) return;
    const tick = () => {
      const r = Math.random();
      const opts = r < 0.4 ? { ratio: +rand(0.55, 0.88).toFixed(2), ageMin: 0 }
                 : r < 0.7 ? { ratio: +rand(0.88, 1.12).toFixed(2), ageMin: 0 }
                 : r < 0.85 ? { needsWeight: true, ageMin: 0 }
                 : { ageMin: 0 };
      const newItem = makeItem(opts);
      setItems(prev => [newItem, ...prev].slice(0, 48));
      setJustInIds(prev => new Set([...prev, newItem.id]));
      setTimeout(() => setJustInIds(prev => { const n = new Set(prev); n.delete(newItem.id); return n; }), 5200);
    };
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [liveOn, signedIn]);

  // age bump (mock only — live ages come from real timestamps)
  useEffect(() => {
    if (LIVE) return;
    const id = setInterval(() => setItems(prev => prev.map(it => ({...it, ageMin: it.ageMin + 10/60}))), 10000);
    return () => clearInterval(id);
  }, []);

  const onSave = (item) => {
    showComingSoon("Saved items", "Personal watchlists need accounts, so saving is gated while the public live feed remains anonymous.");
  };

  // Open detail — in live mode fetch the full item (seller, gallery, evidence).
  const openDetail = (item) => {
    if (!item) { setDetailItem(null); return; }
    detailFocusReturnRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    setDetailItem(item);
    if (LIVE) {
      KS.getItem(item.id)
        .then(full => setDetailItem(d => (d && d.id === item.id ? { ...d, ...full } : d)))
        .catch(() => {});
    }
  };

  const openAdvancedFilter = () => {
    showComingSoon("Advanced filters", "The public MVP keeps the core feed filters live; saved searches and advanced criteria come next.");
  };

  // feed filtering
  const feedMatch = (it, key) => {
    switch (key) {
      case "todays_deals": {
        const cap = dealTierThreshold("todays_deals") ?? TIER.deal.max;
        return it.ratio != null && it.ratio <= cap && Number(it.ageMin) <= 24 * 60;
      }
      case "deep_discount": return it.ratio != null && it.ratio <= TIER.deep_discount.max;
      case "offer":
      case "negotiable": {
        const cap = dealTierThreshold("negotiable") ?? TIER.deal.max;
        return it.bestOffer === true && it.ratio != null && it.ratio <= cap;
      }
      case "deal":  return it.ratio != null && it.ratio <= TIER.deal.max;
      case "steal": return it.ratio != null && it.ratio <= TIER.steal.max;
      case "near": return false;
      case "unscored_gold":
      case "needs": return it.isSolidGold === true && it.ratio == null;
      case "all":   return true;
      default: return true;
    }
  };

  const counts = useMemo(() => {
    if (LIVE) {
      return { ...(browse.counts || {}) };
    }
    const c = {};
    ["todays_deals","deep_discount","steal","deal","negotiable","unscored_gold","all"].forEach(k => { c[k] = items.filter(it => feedMatch(it, k)).length; });
    return c;
  }, [items, browse.counts, tierMetaRevision]);

  const visibleItems = useMemo(() => {
    if (LIVE) return [...browse.items];
    let arr = searchTerm
      ? [...items]
      : items.filter(it => feedMatch(it, feed));
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      arr = arr.filter(it =>
        [it.title, it.itemType, it.seller, it.primaryMetal]
          .filter(Boolean)
          .some(v => String(v).toLowerCase().includes(q))
      );
    }
    // review-required is included by default; the sidebar toggle is an opt-out filter.
    if (!includeReview) {
      arr = arr.filter(it => !it.requiresReview && !it.weightRequiresReview);
    }
    const activeKarats = filters.karat || [];
    const activeTypes = filters.type || [];
    if (activeKarats.length) {
      arr = arr.filter(it => activeKarats.includes(Number(it.karat)));
    }
    if (activeTypes.length) {
      arr = arr.filter(it => activeTypes.includes(it.itemType));
    }
    arr = [...arr];
    if (sort === "newest") arr.sort((a,b) => a.ageMin - b.ageMin);
    else if (sort === "price_to_melt_asc") arr.sort((a,b) => (a.ratio ?? 99) - (b.ratio ?? 99));
    else if (sort === "deal_score_desc") arr.sort((a,b) => (b.valueScore ?? -1) - (a.valueScore ?? -1));
    else if (sort === "listing_time_desc") arr.sort((a,b) => a.ageMin - b.ageMin);
    return arr;
  }, [browse.items, items, feed, includeReview, sort, searchTerm, filters, tierMetaRevision]);

  const related = useMemo(() => {
    if (!detailItem) return [];
    const sourceItems = LIVE ? browse.items : items;
    return sourceItems.filter(it => it.id !== detailItem.id && it.itemType === detailItem.itemType).slice(0, 5);
  }, [detailItem, browse.items, items]);

  const loading = LIVE ? browse.loading && !browse.hasItems : false;
  const error = LIVE ? browse.error : null;
  const searchMeta = LIVE && searchTerm
    ? { totalCount: browse.totalCount ?? visibleItems.length, nextPage: browse.nextPage }
    : null;
  const activeTotal = LIVE
    ? (searchTerm ? browse.totalCount : counts[feed])
    : visibleItems.length;
  const lastAnalyzedLabel = LIVE
    ? lastAnalyzedAge(lastAnalyzedAt, lastAnalyzedNowMs)
    : "mock";
  const handleFeedScroll = useCallback((event) => {
    if (LIVE) browse.saveScrollTop(event.currentTarget.scrollTop);
  }, [browse.saveScrollTop]);

  const onNav = (k) => {
    if (k === "deals") { setActive(k); changeFeed("todays_deals"); }
    else if (k === "browse") { setActive(k); changeFeed("all"); }
    else if (k === "alerts") showComingSoon("Alerts", "Alert rules, channels, saved searches, and alert history are account features coming after launch.");
    else if (k === "saved") showComingSoon("Saved items", "Saved items and saved searches need accounts, so they are paused for the anonymous MVP.");
    else if (k === "live") { setLiveOn(prev => !prev); }
  };

  const showAlerts = tweaks.showAlertsRail;

  return (
    <div className="app" data-entry-cache-revision={FRONTEND_ENTRY_CACHE_REVISION}>
      <Header
        liveOn={liveOn} active={active} setActive={onNav}
        onOpenTweaks={() => setTweaksOpen(v => !v)}
        theme={tweaks.theme}
        onThemeChange={setTheme}
        signedIn={signedIn} onComingSoon={showComingSoon}
        spot={spot} counts={counts}
        searchTerm={searchTerm} onSearch={runSearch} onClearSearch={clearSearch}
        searching={loading && !!searchTerm}
        brandTagline={tweaks.brandTagline}
        user={session.user}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
        onUpdateProfile={onUpdateProfile}
        onCloseAccount={onCloseAccount}
      />
      <div className="app-shell">
        <FilterRail
          liveOn={liveOn} setLiveOn={setLiveOn}
          onOpenRuleBuilder={openAdvancedFilter}
          lastAnalyzedLabel={lastAnalyzedLabel} liveStatus={liveStatus} liveMatchCount={activeTotal}
          filters={filters} setFilters={setFilters}
          signedIn={signedIn} onComingSoon={showComingSoon}
          searchTerm={searchTerm} onSearch={runSearch} onClearSearch={clearSearch}
          onApplyFilters={applyFilters} onResetFilters={resetFilters}
        />

        <main className="feed-wrap">
          <Ticker spot={spot} live={LIVE} tierMetaRevision={tierMetaRevision}/>
          <TierBar
            feed={feed} setFeed={changeFeed} counts={counts}
          />
          <FilterStrip
            onOpenRuleBuilder={openAdvancedFilter}
            signedIn={signedIn}
            filters={filters}
            setFilters={setFilters}
            searchTerm={searchTerm}
            onClearSearch={clearSearch}
            onClearFilters={clearFilters}
          />
          <FeedHeader
            feed={feed}
            sort={LIVE ? browse.effectiveSort : sort}
            setSort={setSort}
            count={activeTotal ?? visibleItems.length}
            liveOn={liveOn}
            view={view}
            setView={setFeedLayout}
            searchTerm={searchTerm}
            searchMeta={searchMeta}
            sortEnabled={!LIVE || browse.sortEnabled}
            sortDisabledReason={searchTerm ? "Search results use backend ordering." : "Deal feeds use fixed listing-time ordering."}
          />
          <div className="feed-body" data-testid="feed-body" ref={feedBodyRef} onScroll={handleFeedScroll}>
            {error ? (
              <ErrorState error={error} onRetry={() => requestRefresh(false)}/>
            ) : loading ? (
              <LoadingGrid/>
            ) : visibleItems.length === 0 ? (
              <EmptyState feed={feed}/>
            ) : view === "grid" ? (
              <div className="grid">
                {visibleItems.map(it => (
                  <ItemCard key={it.id} item={it} justIn={justInIds.has(it.id)} entryDelay={justInDelays.get(it.id) || 0} onOpen={openDetail} signedIn={signedIn} onSave={onSave}/>
                ))}
              </div>
            ) : (
              <ListView items={visibleItems} justInIds={justInIds} justInDelays={justInDelays} onOpen={openDetail} signedIn={signedIn}/>
            )}
            {!loading && !error && visibleItems.length > 0 && (
              <>
                <FeedFooter
                  shown={visibleItems.length}
                  total={activeTotal}
                  hasMore={LIVE && browse.hasMore}
                  loadingMore={LIVE && browse.loadingMore}
                  stale={LIVE && browse.stale}
                  nextPageError={LIVE ? browse.nextPageError : null}
                  onLoadMore={browse.loadMore}
                  onRetryMore={browse.loadMore}
                  searchCapped={LIVE && browse.searchCapped}
                />
                {LIVE && browse.hasMore && !browse.nextPageError ? (
                  <div ref={loadMoreSentinelRef} className="load-more-sentinel" data-testid="load-more-sentinel" aria-hidden="true"/>
                ) : null}
              </>
            )}
          </div>
        </main>

        {showAlerts && <RightRail onComingSoon={showComingSoon}/>}
      </div>

      {detailItem && <DetailDrawer item={detailItem} onClose={() => setDetailItem(null)} onOpen={openDetail} signedIn={signedIn} onSave={onSave} related={related} onComingSoon={showComingSoon} restoreFocusTo={detailFocusReturnRef.current}/>}
      <TweaksPanel visible={tweaksOpen} onClose={() => setTweaksOpen(false)} tweaks={tweaks} setTweaks={setTweaks}/>
      <ComingSoon
        open={!!comingSoon}
        feature={comingSoon && comingSoon.feature}
        detail={comingSoon && comingSoon.detail}
        onClose={closeComingSoon}
      />
    </div>
  );
}

function App() {
  return (
    <SessionBoundary>
      {({ session, openSignIn, signOut, updateProfile, closeAccount }) => (
        <ProductApp
          session={session}
          onSignIn={openSignIn}
          onSignOut={signOut}
          onUpdateProfile={updateProfile}
          onCloseAccount={closeAccount}
        />
      )}
    </SessionBoundary>
  );
}

createRoot(document.getElementById("root")).render(<App/>);

export { App, ProductApp };
